import { useCallback, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { UserStateItemKey, UserStateMap } from "../types/user-state";
import type { ApplicationState, ChecklistItem, Job } from "../types/tracked-job";
import { useUser, useUserState } from "../query/UserProvider";
import { useUpdateUserStateItem } from "../query/user-state/useUpdateUserStateItem";
import { useDeleteUserStateItem } from "../query/user-state/useDeleteUserStateItem";
import { isUserStateConflictError, applyUserStateEntry } from "../query/user-state/userStateOptimisticQuery";
import { userStateQueryKey } from "../query/user-state/userStateQueryKeys";
import { mergeConflictEntryField } from "./tracked-job-conflict-merge";
import { createExternalAppliedJobId } from "../utils/job-tracking/external-applied-job-id";
import { normalizeExternalJobUrl } from "../utils/job-tracking/normalize-external-job-url";
import type { ExternalJobUrlNormalizeFailureReason } from "../types/external-applied-job";
import { resolveTrackedJobRef, timestampExtrasFromJob } from "../utils/job-tracking/resolve-tracked-job-ref";
import { userStateEntryToJob } from "../utils/user-state/extract-jobs-from-user-state";
import { buildJobUserStatePayload as buildJobPayload } from "../utils/user-state/build-job-user-state-payload";

function ensureJobId(job: Job, jobId: string): Job {
  return { ...job, id: jobId };
}

function normalizeJobId(job: Job): string {
  const id = job.id ?? (job as { objectID?: string }).objectID;
  return id ? String(id) : "";
}

/** Reads a stored entry's version, defaulting unsaved/absent entries to 0 — same convention as
 *  `createDefaultUserStateEntry`. Used in place of the old pre-write network prefetch (see the
 *  file-level doc comment below: "version-prefetch simplification"). */
function versionOf(userState: UserStateMap | undefined, itemKey: UserStateItemKey): number {
  const entry = userState?.[itemKey];
  return entry?.version ?? 0;
}

interface UpsertJobEntryResult {
  ok: boolean;
}

/**
 * TEC-404: the writer AND reader for every "save/view/apply/hide a job" interaction site-wide,
 * synced through the TanStack Query layer (`query/user-state/useUpdateUserStateItem.ts` /
 * `useDeleteUserStateItem.ts`) instead of RTK Query (`redux/services/userStateApi.ts`).
 *
 * Phase C: this hook used to dual-write Redux's `jobs` slice alongside the TanStack `userState`
 * cache, for consumers that hadn't migrated yet. Now that every consumer reads `userState`
 * directly (`useHitState.ts`, `useJobState.ts`, the kanban board, HistoryDrawer,
 * HubActivityWidget, ...), the Redux writes were pure duplication and have been removed —
 * `updateUserStateItem`/`deleteUserStateItem`'s own `onMutate`/`onError`/`onSuccess` lifecycle
 * already gives the same optimistic-update-then-reconcile behavior the manual
 * `dispatch()`-then-`store.getState()`-validate scaffolding was hand-rolling. Two real
 * consequences of that, not just mechanical cleanup:
 *
 * 1. `syncJobIsHiddenToBackend` now takes the target `isHidden` value as an explicit parameter
 *    instead of inferring it from Redux state a caller was expected to have already mutated —
 *    there's no more side channel to read it from. Callers pass it directly.
 * 2. `syncJobFieldToBackend`'s `mergeField` callback is gone: the conflict-merge it drove
 *    (re-applying a locally-typed field on top of the server's winning entry) already happens
 *    inside `upsertJobEntry`'s `conflictPatch` handling, which writes the merged entry straight
 *    into the TanStack cache — the callback was only there to replay that merge into Redux too.
 *
 * Conflict-merge behavior (unchanged from the original): a 409 on `syncApplicationStateToBackend`
 * or `syncJobFieldToBackend` (notes/tags/checklist) keeps the caller's locally-written field on top
 * of the server's winning entry rather than accepting the server version wholesale — see
 * `tracked-job-conflict-merge.ts`'s `mergeConflictEntryField`. `syncJobIsHiddenToBackend` and the
 * record/apply "add" paths deliberately do NOT merge — they accept the server's entry wholesale on
 * conflict, matching the original.
 */
export function useUserJobStateSync() {
  const queryClient = useQueryClient();
  const { isAuthenticated } = useUser();
  const { data: userState } = useUserState();
  const updateUserStateItem = useUpdateUserStateItem();
  const deleteUserStateItem = useDeleteUserStateItem();

  const failedSyncItemKeysRef = useRef<Set<UserStateItemKey>>(new Set());

  useEffect(() => {
    if (isAuthenticated) {
      failedSyncItemKeysRef.current.clear();
    }
  }, [isAuthenticated]);

  /**
   * Shared write tail: PUTs `payload` via the TanStack mutation (which itself branches on auth
   * and handles its own optimistic write / rollback / conflict cache update). `conflictPatch`,
   * when given, re-applies that field on top of the server's winning entry on a 409 instead of
   * accepting it wholesale — see file-level doc comment.
   */
  const upsertJobEntry = useCallback(
    async (
      itemKey: UserStateItemKey,
      payload: Record<string, unknown>,
      version: number,
      conflictPatch?: Record<string, unknown>
    ): Promise<UpsertJobEntryResult> => {
      if (failedSyncItemKeysRef.current.has(itemKey)) {
        return { ok: false };
      }

      try {
        await updateUserStateItem.mutateAsync({ itemKey, data: payload, version });
        failedSyncItemKeysRef.current.delete(itemKey);
        return { ok: true };
      } catch (rawError) {
        if (isUserStateConflictError(rawError)) {
          if (conflictPatch) {
            // The mutation's own onError already wrote the server's raw entry into the TanStack
            // cache — overwrite it with the merged entry so the field we just wrote wins over the
            // server's possibly-stale value.
            const merged = mergeConflictEntryField(rawError.data, conflictPatch);
            queryClient.setQueryData<UserStateMap>(userStateQueryKey, (current) =>
              applyUserStateEntry(current ?? {}, itemKey, merged)
            );
          }
          failedSyncItemKeysRef.current.delete(itemKey);
          return { ok: true };
        }
        const status = (rawError as { status?: number } | undefined)?.status;
        if (status !== 409) {
          console.error(`[user-state] Failed to sync ${itemKey}`, rawError);
        }
        if (status === 401) {
          failedSyncItemKeysRef.current.add(itemKey);
        }
        return { ok: false };
      }
    },
    [queryClient, updateUserStateItem]
  );

  const recordViewedJob = useCallback(
    async (job: Job): Promise<boolean> => {
      const jobId = normalizeJobId(job);
      if (!jobId) {
        return false;
      }

      const itemKey = `viewedJob#${jobId}` as const;
      if (userState?.[itemKey]) {
        return true; // Already viewed
      }

      const enrichedJob = {
        ...ensureJobId(job, jobId),
        viewedAt: new Date().toISOString(),
      };

      const result = await upsertJobEntry(
        itemKey,
        buildJobPayload(enrichedJob, { viewedAt: enrichedJob.viewedAt }),
        versionOf(userState, itemKey)
      );
      return result.ok;
    },
    [upsertJobEntry, userState]
  );

  const recordBrowsedJob = useCallback(
    async (job: Job) => {
      const jobId = normalizeJobId(job);
      if (!jobId) {
        return;
      }

      // Product decision, not sync scaffolding: browsed-job tracking is authenticated-only.
      if (!isAuthenticated) {
        return;
      }

      const itemKey = `browsedJob#${jobId}` as const;
      const entry = userState?.[itemKey];

      if (entry && !(entry.version === 0 && entry.updatedAt === null)) {
        // Already browsed, no need to update
        return;
      }

      const enrichedJob = {
        ...ensureJobId(job, jobId),
        browsedAt: new Date().toISOString(),
      };

      await upsertJobEntry(
        itemKey,
        buildJobPayload(enrichedJob, { browsedAt: enrichedJob.browsedAt }),
        versionOf(userState, itemKey)
      );
    },
    [isAuthenticated, upsertJobEntry, userState]
  );

  const removeViewedJobRemote = useCallback(
    async (jobId: string) => {
      const itemKey = `viewedJob#${jobId}` as const;
      if (!userState?.[itemKey]) {
        return;
      }

      try {
        await deleteUserStateItem.mutateAsync({
          itemKey,
          version: userState[itemKey]?.version,
        });
      } catch {
        // useDeleteUserStateItem's own onError already restores the previous entry (or the
        // server's winning entry on conflict) into the TanStack cache.
      }
    },
    [deleteUserStateItem, userState]
  );

  const syncApplicationStateToBackend = useCallback(
    async (jobId: string, applicationState?: ApplicationState, job?: Job): Promise<boolean> => {
      const appliedItemKey = `appliedJob#${jobId}` as const;
      const appliedEntry = userState?.[appliedItemKey];
      const isApplied = Boolean(appliedEntry);

      // If applicationState was passed explicitly, use it (most reliable); otherwise fall back to
      // whatever's already stored for this job.
      const stateToSync = applicationState ?? userStateEntryToJob(appliedEntry)?.applicationState;
      if (stateToSync === undefined) {
        return false;
      }

      // Get job data from the parameter, the appliedJob# entry, or any other tracked entry.
      let jobData = job;
      if (!jobData) {
        jobData = userStateEntryToJob(appliedEntry) ?? undefined;
      }
      if (!jobData) {
        const savedEntry = userState?.[`savedJob#${jobId}` as const];
        const viewedEntry = userState?.[`viewedJob#${jobId}` as const];
        const browsedEntry = userState?.[`browsedJob#${jobId}` as const];
        jobData = userStateEntryToJob(savedEntry ?? viewedEntry ?? browsedEntry) ?? undefined;
      }
      if (!jobData) {
        console.warn('[syncApplicationStateToBackend] Cannot find job data:', { jobId });
        return false;
      }

      jobData = { ...jobData, id: jobId, applicationState: stateToSync };

      const payload = buildJobPayload(ensureJobId(jobData, jobId), {
        applicationState: stateToSync,
        ...(isApplied ? {} : { appliedAt: new Date().toISOString() }), // Add appliedAt if creating new applied job
      });

      const result = await upsertJobEntry(appliedItemKey, payload, versionOf(userState, appliedItemKey), {
        applicationState: stateToSync,
      });
      if (!result.ok) {
        console.error('[syncApplicationStateToBackend] Failed to sync applicationState:', {
          jobId,
          applicationState: stateToSync,
        });
      }
      return result.ok;
    },
    [upsertJobEntry, userState]
  );

  const setAppliedJobState = useCallback(
    async (job: Job, applied: boolean): Promise<boolean> => {
      const jobId = normalizeJobId(job);
      if (!jobId) {
        return false;
      }

      const itemKey = `appliedJob#${jobId}` as const;

      if (applied) {
        if (userState?.[itemKey]) {
          return true; // Already applied
        }

        const enrichedJob = {
          ...ensureJobId(job, jobId),
          appliedAt: new Date().toISOString(),
        };

        const result = await upsertJobEntry(
          itemKey,
          buildJobPayload(enrichedJob, { appliedAt: enrichedJob.appliedAt }),
          versionOf(userState, itemKey)
        );
        return result.ok;
      } else {
        if (!userState?.[itemKey]) {
          return true; // Not applied, consider removal success
        }

        try {
          await deleteUserStateItem.mutateAsync({
            itemKey,
            version: userState[itemKey]?.version,
          });
          return true;
        } catch {
          // useDeleteUserStateItem's own onError already restores the entry on failure.
          return false;
        }
      }
    },
    [deleteUserStateItem, upsertJobEntry, userState]
  );

  const addExternalAppliedJob = useCallback(
    async (
      rawUrl: string,
      optionalTitle?: string
    ): Promise<
      | { success: true; jobId: string; jobTitle: string }
      | { success: false; reason: ExternalJobUrlNormalizeFailureReason | "sync_failed" }
    > => {
      const normalized = normalizeExternalJobUrl(rawUrl);
      if (!normalized.ok) {
        return { success: false, reason: normalized.reason };
      }
      const id = createExternalAppliedJobId();
      const trimmedTitle = optionalTitle?.trim();
      const job: Job = {
        id,
        ...(trimmedTitle ? { title: trimmedTitle, normalisedTitle: trimmedTitle } : {}),
        externalUrl: normalized.url,
        applicationState: null,
      };
      const ok = await setAppliedJobState(job, true);
      return ok ? { success: true, jobId: id, jobTitle: trimmedTitle || '' } : { success: false, reason: "sync_failed" };
    },
    [setAppliedJobState]
  );

  const syncJobIsHiddenToBackend = useCallback(
    async (jobId: string, isHidden: boolean): Promise<void> => {
      if (!jobId) return;
      const ref = resolveTrackedJobRef(jobId, userState);
      if (!ref) return;

      const { itemKey, job } = ref;
      const updatedJob = { ...job, isHidden };

      await upsertJobEntry(
        itemKey,
        buildJobPayload(ensureJobId(updatedJob, jobId), timestampExtrasFromJob(updatedJob)),
        versionOf(userState, itemKey)
      );
    },
    [upsertJobEntry, userState]
  );

  /**
   * Shared tail of every "edit one field on a tracked job" mutation (notes, tags, checklist, ...):
   * upserts `patch` layered onto the job's other stored fields, preserving that field on top of
   * the server's winning entry on a version conflict (via `upsertJobEntry`'s `conflictPatch`).
   */
  const syncJobFieldToBackend = useCallback(
    async (jobId: string, patch: Record<string, unknown>): Promise<void> => {
      const ref = resolveTrackedJobRef(jobId, userState);
      if (!ref) return;

      const { itemKey, job } = ref;

      await upsertJobEntry(
        itemKey,
        buildJobPayload({ ...ensureJobId(job, jobId), ...patch }, timestampExtrasFromJob(job)),
        versionOf(userState, itemKey),
        patch
      );
    },
    [upsertJobEntry, userState]
  );

  const setJobNotes = useCallback(
    async (jobId: string, notes: string): Promise<void> => {
      if (!jobId) return;
      await syncJobFieldToBackend(jobId, { notes });
    },
    [syncJobFieldToBackend]
  );

  const setJobTags = useCallback(
    async (jobId: string, tags: string[]): Promise<void> => {
      if (!jobId) return;
      await syncJobFieldToBackend(jobId, { tags });
    },
    [syncJobFieldToBackend]
  );

  const setJobChecklist = useCallback(
    async (jobId: string, checklist: ChecklistItem[]): Promise<void> => {
      if (!jobId) return;
      await syncJobFieldToBackend(jobId, { checklist });
    },
    [syncJobFieldToBackend]
  );

  return {
    recordViewedJob,
    recordBrowsedJob,
    removeViewedJob: removeViewedJobRemote,
    setAppliedJobState,
    syncApplicationStateToBackend,
    syncJobIsHiddenToBackend,
    addExternalAppliedJob,
    setJobNotes,
    setJobTags,
    setJobChecklist,
  };
}
